import React, { useCallback, useContext, useMemo } from 'react';
import { Form } from 'react-final-form';
import { PricebookBundle, IntervalTypeEnum } from '../../models/PricebookData';
import { PaidByEnum } from '../../models/LicenseData';
import { RevShareEntry, RevShareResult } from '../../models/RevShareData';
import { AdminTierTypeEnum } from '../../services/drillDown';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { useLoading } from '../../hooks/useLoading';
import { useMutation } from '@aesop-fables/scrinium';
import { isAxiosError } from 'axios';
import { SaveRevenueShareEntry } from '../../data/rev-share/mutations';
import {
  RevShareFormValues,
  getEntriesForPricebook,
  findNearestParentEntry,
} from '../../data/rev-share/helpers/revShareSettingsUtils';
import {
  getRevShareFormInitialValues,
  validateRevShareForm,
} from '../../data/rev-share/helpers/revShareFormUtils';
import { validateDateOverlap } from '../../data/rev-share/helpers/revShareEntryUtils';
import RightDrawer from '../../components/RightDrawer';
import RevShareEntryForm from './RevShareEntryForm';

interface RevShareEntryDrawerProps {
  open: boolean;
  onClose: () => void;
  pricebook: PricebookBundle | null;
  existingEntry: RevShareEntry | null;
  revShareSettings: RevShareResult | undefined;
  adminTier: AdminTierTypeEnum;
}

const RevShareEntryDrawer: React.FC<RevShareEntryDrawerProps> = ({
  open,
  onClose,
  pricebook,
  existingEntry,
  revShareSettings,
  adminTier,
}) => {
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const saveEntry = useMutation(new SaveRevenueShareEntry());

  const inheritedRevShareEntry = useMemo(
    () => findNearestParentEntry(revShareSettings, pricebook?.pricebook.id ?? 0, adminTier),
    [revShareSettings, pricebook, adminTier],
  );

  const activePrice = useMemo(
    () => pricebook?.prices.find(p => p.intervalType !== IntervalTypeEnum.None),
    [pricebook],
  );

  const pricebookPrice = activePrice ? activePrice.price / 100 : 0;

  const isEditing = Boolean(existingEntry);
  const isOrgPays = pricebook?.pricebook.paidBy === PaidByEnum.Org;
  const paidByLabel = isOrgPays ? 'Organization pays' : 'Member pays';

  const isViewOnly = Boolean(
    existingEntry?.tier && existingEntry.tier.toLowerCase() !== adminTier.toLowerCase(),
  );

  const drawerTitle = isViewOnly
    ? 'View fee share'
    : isEditing
      ? 'Edit fee share'
      : 'Set fee share';

  const initialValues = useMemo(() => {
    if (!pricebook) return undefined;
    return getRevShareFormInitialValues(
      pricebook.pricebook.id,
      existingEntry,
      inheritedRevShareEntry,
      adminTier,
      activePrice,
    );
  }, [pricebook, existingEntry, inheritedRevShareEntry, adminTier, activePrice]);

  const validateForm = useMemo(() => validateRevShareForm(pricebookPrice), [pricebookPrice]);

  const handleSave = useCallback(
    async (values: RevShareFormValues) => {
      try {
        setLoading(true);

        if (pricebook && values.effectiveTimestamp) {
          const existingEntries = getEntriesForPricebook(
            revShareSettings,
            pricebook.pricebook.id,
          ).filter(e => !e.tier || e.tier.toLowerCase() === adminTier.toLowerCase());
          const newEffective = new Date(values.effectiveTimestamp);
          const newExpiration =
            values.setEndDate === 'Yes' && values.expireTimestamp
              ? new Date(values.expireTimestamp)
              : null;

          const overlapResult = validateDateOverlap(
            newEffective,
            newExpiration,
            existingEntries,
            values.existingEntryId,
          );

          if (!overlapResult.valid) {
            setToast(
              new Toast({
                severity: 'error',
                open: true,
                message:
                  overlapResult.errorMessage ?? 'Date range conflicts with an existing entry.',
                autoHideDuration: 6000,
              }),
            );
            setLoading(false);
            return;
          }
        }

        await saveEntry.action({ formValues: values, adminTier });

        setToast(
          new Toast({
            severity: 'success',
            open: true,
            message: isEditing
              ? `Fee share saved for ${pricebook?.pricebook.pricebookName}!`
              : `Fee share set for ${pricebook?.pricebook.pricebookName}!`,
            autoHideDuration: 6000,
          }),
        );
        onClose();
      } catch (err) {
        console.error('Failed to save fee share entry:', err);
        const errorMessage =
          (isAxiosError<{ message?: string }>(err) && err.response?.data?.message) ||
          'Failed to save fee share entry.';
        setToast(
          new Toast({
            severity: 'error',
            open: true,
            message: errorMessage,
            autoHideDuration: 6000,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [saveEntry, adminTier, isEditing, pricebook, revShareSettings, setLoading, onClose, setToast],
  );

  if (!pricebook) {
    return <RightDrawer open={false} onClose={onClose} />;
  }

  return (
    <RightDrawer open={open} onClose={onClose} smallTitle={paidByLabel} title={drawerTitle}>
      <Form<RevShareFormValues>
        initialValues={initialValues}
        onSubmit={handleSave}
        destroyOnUnregister={false}
        validate={validateForm}>
        {({ handleSubmit }) => (
          <RevShareEntryForm
            handleSubmit={handleSubmit}
            pricebook={pricebook}
            revShareSettings={revShareSettings}
            activePrice={activePrice}
            adminTier={adminTier}
            isViewOnly={isViewOnly}
            onCancel={onClose}
          />
        )}
      </Form>
    </RightDrawer>
  );
};

export default RevShareEntryDrawer;
